import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Chart from 'react-apexcharts';
import PageTitle from '../../FormElements/PageTitle';
import getLandingImageHeight from '../../../utils/getLandingImageHeight';
import { useTheme } from '../../../contexts/ThemeContext';

const WINDOW_OPTIONS = [
	{ value: '0', label: 'Current season (YTD)' },
	{ value: '1', label: 'Last year' },
	{ value: '2', label: 'Last 2 years' },
	{ value: '3', label: 'Last 3 years' }
];

const POSITION_OPTIONS = ['QB', 'RB', 'WR', 'TE'];

/** Compress low Y range so the plot uses vertical space; matches y-axis tick formatter. */
function toBrokenYAxis(value) {
	if (value <= 20) return value * 0.25;
	return 5 + (value - 20);
}

function median(values) {
	if (!values.length) return 0;
	const sorted = [...values].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 !== 0
		? sorted[mid]
		: (sorted[mid - 1] + sorted[mid]) / 2;
}

function average(values) {
	if (!values.length) return 0;
	return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function buildPaddedRange(values, { minFloor = 0, padRatio = 0.12, minSpan = 2 } = {}) {
	if (!values.length) {
		return { min: minFloor, max: minFloor + minSpan };
	}

	const rawMin = Math.min(...values);
	const rawMax = Math.max(...values);
	const span = Math.max(rawMax - rawMin, minSpan);
	const pad = span * padRatio;
	const min = Math.max(minFloor, rawMin - pad);
	const max = rawMax + pad;

	return { min, max: max <= min ? min + minSpan : max };
}

const emptyAnnotationLabel = {
	text: '',
	borderWidth: 0,
	style: {
		fontSize: '1px',
		color: 'transparent',
		background: 'transparent'
	}
};

const quadrantLabelStyle = {
	fontSize: '12px',
	fontWeight: 700,
	color: '#1f2937',
	background: 'rgba(255,255,255,0.92)',
	padding: {
		left: 8,
		right: 8,
		top: 3,
		bottom: 3
	}
};

function buildQuadrantPointAnnotations(
	xMid,
	yMidBroken,
	xMin = 0,
	xMax = 40,
	yMin = 0,
	yMax = 35
) {
	const inset = 0.24;
	const xPad = 0.6;
	const yPad = 0.6;
	const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
	const leftX = clamp(xMid * inset, xMin + xPad, xMax - xPad);
	const rightX = clamp(
		xMid + (xMax - xMid) * (1 - inset),
		xMin + xPad,
		xMax - xPad
	);
	const bottomY = clamp(yMidBroken * inset, yMin + yPad, yMax - yPad);
	const topY = clamp(
		yMidBroken + (yMax - yMidBroken) * (1 - inset),
		yMin + yPad,
		yMax - yPad
	);

	return [
		{
			x: leftX,
			y: bottomY,
			marker: { size: 0, strokeWidth: 0, fillColor: '#ffffff', strokeColor: '#ffffff' },
			label: {
				text: 'Duds',
				textAnchor: 'start',
				offsetX: 6,
				offsetY: -70,
				borderColor: '#e2e8f0',
				borderWidth: 1,
				borderRadius: 6,
				style: quadrantLabelStyle
			}
		},
		{
			x: leftX,
			y: topY,
			marker: { size: 0, strokeWidth: 0, fillColor: '#ffffff', strokeColor: '#ffffff' },
			label: {
				text: 'Spikers',
				textAnchor: 'start',
				offsetX: 6,
				offsetY: 50,
				borderColor: '#e2e8f0',
				borderWidth: 1,
				borderRadius: 6,
				style: quadrantLabelStyle
			}
		},
		{
			x: rightX,
			y: bottomY,
			marker: { size: 0, strokeWidth: 0, fillColor: '#ffffff', strokeColor: '#ffffff' },
			label: {
				text: 'Floors',
				textAnchor: 'end',
				offsetX: 100,
				offsetY: -70,
				borderColor: '#e2e8f0',
				borderWidth: 1,
				borderRadius: 6,
				style: quadrantLabelStyle
			}
		},
		{
			x: rightX,
			y: topY,
			marker: { size: 0, strokeWidth: 0, fillColor: '#ffffff', strokeColor: '#ffffff' },
			label: {
				text: 'Studs',
				textAnchor: 'end',
				offsetX: 100,
				offsetY: 50,
				borderColor: '#e2e8f0',
				borderWidth: 1,
				borderRadius: 6,
				style: quadrantLabelStyle
			}
		}
	];
}

export default function SpikeVolatilityIndexPage() {
	const { isDarkMode } = useTheme();
	const [positionFilter, setPositionFilter] = useState('QB');
	const [abilityWindow, setAbilityWindow] = useState('3');
	const [backendPlayerMetrics, setBackendPlayerMetrics] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [showPlayerLabels, setShowPlayerLabels] = useState(true);
	const showPlayerLabelsRef = useRef(showPlayerLabels);
	showPlayerLabelsRef.current = showPlayerLabels;
	const chartComponentRef = useRef(null);
	const landingImageSize = getLandingImageHeight();
	const rawChartHeight = landingImageSize.height || 650;
	const chartHeight = Math.max(320, rawChartHeight - 44);

	useEffect(() => {
		let isCancelled = false;

		const fetchVolatilityStats = async () => {
			setIsLoading(true);
			try {
				const params = new URLSearchParams({
					lookback_years: abilityWindow,
					position: positionFilter
				});
				const response = await fetch(
					`/api/v1/player_volatility_stats?${params.toString()}`
				);
				if (!response.ok) throw new Error('Failed to fetch volatility stats');
				const payload = await response.json();
				const rows = Array.isArray(payload?.data) ? payload.data : [];
				if (isCancelled) return;

				setBackendPlayerMetrics(
					rows.map((row) => ({
						name: row.player_name,
						position: row.position,
						x: Number(row.all_game_avg),
						y: Number(row.best_game_score)
					}))
				);
			} catch (_error) {
				if (isCancelled) return;
				setBackendPlayerMetrics([]);
			} finally {
				if (!isCancelled) setIsLoading(false);
			}
		};

		fetchVolatilityStats();

		return () => {
			isCancelled = true;
		};
	}, [abilityWindow, positionFilter]);

	const playerMetrics = backendPlayerMetrics;

	const scatterSeries = useMemo(
		() => [
			{
				name: 'Players',
				data: playerMetrics.map((player) => ({
					x: player.x,
					y: toBrokenYAxis(player.y),
					yOriginal: player.y,
					name: player.name,
					position: player.position
				}))
			}
		],
		[playerMetrics]
	);

	const avgAllGamesX = useMemo(
		() => average(playerMetrics.map((p) => p.x)),
		[playerMetrics]
	);

	const medianBestGameY = useMemo(
		() => median(playerMetrics.map((p) => p.y)),
		[playerMetrics]
	);

	const yMidBroken = useMemo(
		() => toBrokenYAxis(medianBestGameY),
		[medianBestGameY]
	);

	const xMid = Number(avgAllGamesX.toFixed(2));
	const axisBounds = useMemo(() => {
		const xValues = playerMetrics.map((player) => player.x).filter((value) => Number.isFinite(value));
		const yValuesBroken = playerMetrics
			.map((player) => toBrokenYAxis(player.y))
			.filter((value) => Number.isFinite(value));

		const xRange = buildPaddedRange(xValues, { minFloor: 0, padRatio: 0.15, minSpan: 3 });
		const yRange = buildPaddedRange(yValuesBroken, { minFloor: 0, padRatio: 0.15, minSpan: 3 });

		return {
			xAxisMin: Number(xRange.min.toFixed(2)),
			xAxisMax: Number(xRange.max.toFixed(2)),
			yAxisMin: Number(yRange.min.toFixed(2)),
			yAxisMax: Number(yRange.max.toFixed(2))
		};
	}, [playerMetrics]);

	const { xAxisMin, xAxisMax, yAxisMin, yAxisMax } = axisBounds;
	const windowLabel =
		WINDOW_OPTIONS.find((option) => option.value === abilityWindow)?.label ||
		'Last 3 years';
	const xAxisTitle = ['0', '1'].includes(abilityWindow)
		? 'Season average (PPG)'
		: 'Best season average (PPG)';

	const chartOptions = useMemo(() => {
		const xFills = [
			{
				x: xAxisMin,
				x2: xMid,
				fillColor: '#6366f1',
				opacity: 0.07,
				borderWidth: 0,
				borderColor: 'transparent',
				label: emptyAnnotationLabel
			},
			{
				x: xMid,
				x2: xAxisMax,
				fillColor: '#22c55e',
				opacity: 0.06,
				borderWidth: 0,
				borderColor: 'transparent',
				label: emptyAnnotationLabel
			}
		];

		const yFills = [
			{
				y: yAxisMin,
				y2: yMidBroken,
				fillColor: '#64748b',
				opacity: 0.06,
				borderWidth: 0,
				borderColor: 'transparent',
				width: '100%',
				label: emptyAnnotationLabel
			},
			{
				y: yMidBroken,
				y2: yAxisMax,
				fillColor: '#a855f7',
				opacity: 0.06,
				borderWidth: 0,
				borderColor: 'transparent',
				width: '100%',
				label: emptyAnnotationLabel
			}
		];

		const xLines = [
			{
				x: xMid,
				borderColor: '#2563eb',
				strokeDashArray: 4,
				borderWidth: 1,
				label: emptyAnnotationLabel
			}
		];

		const yLines = [
			{
				y: yMidBroken,
				borderColor: '#2563eb',
				strokeDashArray: 4,
				borderWidth: 1,
				fillColor: 'transparent',
				opacity: 0,
				width: '100%',
				label: emptyAnnotationLabel
			}
		];

		const quadrantPoints = buildQuadrantPointAnnotations(
			xMid,
			yMidBroken,
			xAxisMin,
			xAxisMax,
			yAxisMin,
			yAxisMax
		);

		const tooltipShell = isDarkMode
			? 'background:#151515;color:#fefefe;border:1px solid #333333;box-shadow:0 10px 15px -3px rgba(0,0,0,0.5),0 4px 6px -4px rgba(0,0,0,0.4);'
			: 'background:#ffffff;color:#111827;border:1px solid #e5e7eb;box-shadow:0 10px 15px -3px rgba(0,0,0,0.08),0 4px 6px -4px rgba(0,0,0,0.06);';
		const tooltipMuted = isDarkMode ? '#d1d5db' : '#4b5563';

		const syncDataLabelVisibility = (chartCtx) => {
			const enabled = showPlayerLabelsRef.current;
			if (chartCtx.w?.config?.dataLabels?.enabled !== enabled) {
				chartCtx.updateOptions({ dataLabels: { enabled } }, false, false);
			}
		};

		return {
			chart: {
				type: 'scatter',
				background: 'transparent',
				zoom: { enabled: false },
				toolbar: { show: false },
				events: {
					// render()/mount() resolves asynchronously; layout effects can run too early.
					mounted: syncDataLabelVisibility,
					// react-apexcharts merges full options after series/filter updates (enabled:true in props).
					updated: syncDataLabelVisibility
				}
			},
			xaxis: {
				min: xAxisMin,
				max: xAxisMax,
				tickAmount: 8,
				axisBorder: {
					show: false
				},
				labels: {
					trim: true,
					hideOverlappingLabels: true,
					style: {
						fontSize: '11px',
						// String applies to every tick; a one-element array only colors index 0 on x-axis.
						colors: isDarkMode ? '#d1d5db' : '#374151'
					}
				},
				title: {
					text: xAxisTitle,
					offsetY: 4,
					style: {
						fontSize: '12px',
						fontWeight: 500,
						color: isDarkMode ? '#f9fafb' : '#111827'
					}
				}
			},
			yaxis: {
				min: yAxisMin,
				max: yAxisMax,
				tickAmount: 7,
				labels: {
					formatter: (value) => {
						if (value <= 5) return Math.round(value * 4).toString();
						return Math.round(value + 15).toString();
					},
					style: {
						fontSize: '11px',
						colors: isDarkMode ? '#d1d5db' : '#374151'
					}
				},
				title: {
					text: 'Best game score (PPG)',
					style: {
						fontSize: '12px',
						fontWeight: 500,
						color: isDarkMode ? '#f9fafb' : '#111827'
					}
				}
			},
			annotations: {
				xaxis: [...xFills, ...xLines],
				yaxis: [...yFills, ...yLines],
				points: quadrantPoints,
				texts: [],
				images: []
			},
			grid: {
				borderColor: isDarkMode ? '#374151' : '#e5e7eb'
			},
			markers: {
				size: 6,
				strokeWidth: 1
			},
			dataLabels: {
				// Always true here so react-apexcharts' options JSON stays stable when toggling
				// labels (otherwise it calls updateOptions(fullConfig) and redraws markers).
				// Visibility is synced on mounted/updated + useLayoutEffect on toggle.
				enabled: true,
				offsetY: -10,
				style: {
					fontSize: '10px',
					fontWeight: 500,
					colors: [isDarkMode ? '#f9fafb' : '#111827']
				},
				background: {
					enabled: false
				},
				dropShadow: {
					enabled: true,
					top: 1,
					left: 1,
					blur: 1,
					opacity: 0.65,
					color: isDarkMode ? '#000000' : '#ffffff'
				},
				formatter: (_value, opts) => {
					const point =
						opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex];
					return point.name;
				}
			},
			tooltip: {
				cssClass: isDarkMode ? 'spike-volatility-tooltip--dark' : undefined,
				custom: ({ seriesIndex, dataPointIndex, w }) => {
					const point = w.config.series[seriesIndex].data[dataPointIndex];
					return `<div style="padding:8px 10px;border-radius:8px;${tooltipShell}">
					<div style="font-weight:600;">${point.name}</div>
					<div style="font-size:12px;color:${tooltipMuted};">${point.position}</div>
					<div style="font-size:12px;color:${tooltipMuted};">Window: ${windowLabel}</div>
					<div style="font-size:12px;color:${tooltipMuted};">All-game average (PPG): ${point.x}</div>
					<div style="font-size:12px;color:${tooltipMuted};">Best game score (PPG): ${point.yOriginal}</div>
				</div>`;
				}
			},
			theme: {
				// Keep Apex in light mode to avoid dark-theme overlay artifacts;
				// component-level colors already handle dark-mode readability.
				mode: 'light'
			}
		};
	}, [isDarkMode, xAxisMin, xAxisMax, yAxisMin, yAxisMax, xMid, yMidBroken, windowLabel, xAxisTitle]);

	useLayoutEffect(() => {
		const apexChart = chartComponentRef.current?.chart;
		if (!apexChart) return;
		if (apexChart.w?.config?.dataLabels?.enabled !== showPlayerLabels) {
			apexChart.updateOptions({ dataLabels: { enabled: showPlayerLabels } }, false, false);
		}
	}, [showPlayerLabels]);

	return (
		<div className="container p-4 pt-4 lg:py-16 md:px-20 text-gray-800 dark:text-dark-text">
			<div className="relative">
				<PageTitle title="Player Volatility Index" />
				<div className="mt-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3 lg:absolute lg:right-0 lg:top-1 lg:mt-0">
					<div className="flex items-center gap-2">
						<label className="text-sm font-medium text-gray-700 dark:text-gray-200">Period:</label>
						<select
							className="border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card rounded-md px-3 py-1.5 text-sm text-gray-800 dark:text-dark-text"
							value={abilityWindow}
							onChange={(event) => setAbilityWindow(event.target.value)}
						>
							{WINDOW_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="flex items-center gap-2">
						<label className="text-sm font-medium text-gray-700 dark:text-gray-200">Position:</label>
						<select
							className="border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card rounded-md px-3 py-1.5 text-sm text-gray-800 dark:text-dark-text"
							value={positionFilter}
							onChange={(event) => setPositionFilter(event.target.value)}
						>
							{POSITION_OPTIONS.map((position) => (
								<option key={position} value={position}>
									{position}
								</option>
							))}
						</select>
					</div>
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium text-gray-700 dark:text-gray-200">
							Show names
						</span>
						<div className="flex items-center">
							<input
								type="checkbox"
								checked={showPlayerLabels}
								onChange={(event) => setShowPlayerLabels(event.target.checked)}
								className="hidden"
								id="spikeVolatilityShowNamesSwitch"
							/>
							<label
								htmlFor="spikeVolatilityShowNamesSwitch"
								className="cursor-pointer select-none relative"
							>
								<div
									className={`w-8 h-4 md:w-12 md:h-6 rounded-full shadow-inner ${showPlayerLabels
										? 'bg-gray-800'
										: 'bg-gray-300'}`}
								/>
								<div
									className={`dot absolute top-0 left-0 w-4 h-4 md:w-6 md:h-6 bg-white rounded-full shadow transition transform ${showPlayerLabels
										? 'translate-x-full border border-gray-600'
										: 'border border-gray-300'}`}
								/>
							</label>
						</div>
					</div>
					<Link
						to="/best-ball-zone"
						className="shrink-0 sm:ml-2 md:ml-4 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
					>
						← Best Ball Zone
					</Link>
				</div>
			</div>

			<div
				className="mt-5 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-xl p-3 md:p-4 shadow-sm overflow-visible relative"
				style={landingImageSize}
			>
				{isLoading ? (
					<div
						className="flex items-center justify-center"
						style={{ minHeight: chartHeight }}
					>
						<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-indigo-500 dark:border-dark-border dark:border-t-indigo-400" />
					</div>
				) : playerMetrics.length > 0 ? (
					<Chart
						ref={chartComponentRef}
						key={isDarkMode ? 'dark' : 'light'}
						options={chartOptions}
						series={scatterSeries}
						type="scatter"
						height={chartHeight}
					/>
				) : (
					<div
						className="flex items-center justify-center text-gray-500 dark:text-gray-300 text-sm border border-dashed border-gray-200 dark:border-dark-border rounded-lg"
						style={{ minHeight: chartHeight }}
					>
						No players match this filter.
					</div>
				)}
			</div>
		</div>
	);
}
